import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageMeta from '../components/PageMeta';
import Lightbox from '../components/Lightbox';
import OrderForm from '../components/OrderForm';
import { imageUrl } from '../lib/images';
import { fetchCollection, fetchSide, FALLBACK_YDELSER, FALLBACK_SIDER, getSlug } from '../lib/cms';

export default function Ydelser() {
  const [ydelser, setYdelser] = useState(FALLBACK_YDELSER);
  const [sideData, setSideData] = useState(FALLBACK_SIDER.ydelser || {});
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  useEffect(() => {
    async function loadData() {
      const [ydData, sData] = await Promise.all([
        fetchCollection('ydelser', FALLBACK_YDELSER),
        fetchSide('ydelser')
      ]);
      if (Array.isArray(ydData) && ydData.length > 0) {
        setYdelser(ydData);
      }
      if (sData) {
        setSideData(sData);
      }
    }
    loadData();
  }, []);

  const openImage = (imgs, index = 0) => {
    setLightboxImages(imgs);
    setCurrentImgIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      <PageMeta
        title={sideData.titel || "Ydelser & Produkter - Duus Rustfri"}
        description={sideData.meta_beskrivelse || "Oversigt over ydelser hos Duus Rustfri i Vissenbjerg. Specialfremstilling, foderkrybber og trug, svejsning og rørarbejde, smedeopgaver og havebede."}
      />

      {/* Hero Header */}
      <section className="bg-slate-950 text-white py-16 sm:py-24 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 border border-slate-800 text-sky-400 text-xs font-semibold uppercase tracking-wider mb-4">
              <i className="fa-solid fa-wrench text-sky-400" aria-hidden="true" />
              <span>{sideData.badge || 'Kompetencer & Arbejdsområder'}</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white mb-4">
              {sideData.overskrift || 'Ydelser & Specialløsninger'}
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              {sideData.underoverskrift || 'Hos Duus Rustfri leverer vi gedigent metalhåndværk i rustfrit stål, sort stål og aluminium. Fra enkeltstående specialopgaver til mindre serier og reparationer.'}
            </p>
          </div>
        </div>
      </section>

      {/* Ydelser Grid */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {ydelser.map((ydelse, index) => {
            const isEven = index % 2 === 1;
            const img = ydelse.billede?.[0];

            return (
              <div
                key={ydelse.id || ydelse.slug}
                id={ydelse.slug}
                className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className={`grid grid-cols-1 lg:grid-cols-12 items-center`}>
                  {/* Image Column */}
                  <div
                    className={`lg:col-span-6 relative aspect-[16/11] lg:aspect-auto lg:h-full bg-slate-900 cursor-pointer overflow-hidden group ${
                      isEven ? 'lg:order-2' : ''
                    }`}
                    onClick={() => openImage(ydelse.billede || [img])}
                  >
                    <img
                      src={imageUrl(img, 'width=900,quality=85,format=auto')}
                      alt={ydelse.titel}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 min-h-[300px]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition" />
                    <div className="absolute bottom-4 left-4 text-white text-xs font-medium flex items-center gap-1.5 bg-slate-950/80 px-3 py-1.5 rounded-lg border border-slate-800">
                      <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                      <span>Se foto i stor visning</span>
                    </div>
                  </div>

                  {/* Content Column */}
                  <div className={`lg:col-span-6 p-8 sm:p-12 space-y-6 ${isEven ? 'lg:order-1' : ''}`}>
                    <div className="space-y-2">
                      <span className="text-xs font-mono font-bold uppercase text-sky-700 bg-sky-50 px-2.5 py-1 rounded border border-sky-200">
                        Arbejdsområde {index + 1}
                      </span>
                      <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-950 tracking-tight">
                        {ydelse.titel}
                      </h2>
                    </div>

                    <p className="text-slate-600 leading-relaxed text-base">
                      {ydelse.kort_beskrivelse}
                    </p>

                    <div
                      className="text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-4"
                      dangerouslySetInnerHTML={{ __html: ydelse.beskrivelse }}
                    />

                    {/* Material & Key Points */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100 text-xs">
                      {ydelse.materialer && (
                        <div>
                          <span className="font-semibold text-slate-900 block mb-0.5">Materialer:</span>
                          <span className="text-slate-600">{ydelse.materialer}</span>
                        </div>
                      )}
                      {ydelse.fordele && (
                        <div>
                          <span className="font-semibold text-slate-900 block mb-0.5">Karakteristika:</span>
                          <span className="text-slate-600">{ydelse.fordele}</span>
                        </div>
                      )}
                    </div>

                    {/* CTAs */}
                    <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                      <Link
                        to={`/ydelser/${getSlug(ydelse)}`}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm transition shadow-sm text-center"
                      >
                        <span>Gå til undersiden</span>
                        <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                      </Link>

                      <a
                        href="#bestil-ydelse"
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-sky-50 hover:bg-sky-100 text-sky-800 font-semibold text-sm border border-sky-200 transition text-center"
                      >
                        <span>Forespørg på denne ydelse</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Inquiry Form at bottom */}
      <section id="bestil-ydelse" className="py-16 sm:py-24 bg-slate-950 text-white scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <OrderForm
            title="Bestil eller få et uforpligtende tilbud på en ydelse"
            subtitle="Vælg din ønskede ydelse ovenfor eller beskriv dine ønsker direkte herunder."
          />
        </div>
      </section>

      {/* Lightbox Modal */}
      <Lightbox
        isOpen={lightboxOpen}
        images={lightboxImages}
        currentIndex={currentImgIndex}
        onClose={() => setLightboxOpen(false)}
        onPrev={() => setCurrentImgIndex((prev) => (prev > 0 ? prev - 1 : lightboxImages.length - 1))}
        onNext={() => setCurrentImgIndex((prev) => (prev < lightboxImages.length - 1 ? prev + 1 : 0))}
        onSelectIndex={(idx) => setCurrentImgIndex(idx)}
      />
    </div>
  );
}
